import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TrialCountdown from '../components/TrialCountdown';
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  TrendingUp, 
  User, 
  ArrowRight, 
  Eye, 
  RotateCcw,
  Brain,
  Heart,
  Sparkles,
  Star,
  Zap,
  Target,
  Shield,
  Activity,
  BarChart3
} from 'lucide-react';
import api from '../utils/api';
import { getSpecialtyColor } from '../utils/forceRefresh';

const Assessments = () => {
  const [assessments, setAssessments] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [selectedTherapist, setSelectedTherapist] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      console.log('=== ASSESSMENTS PAGE DEBUG ===');
      console.log('Fetching therapists...');
      
      // Fetch therapists
      const therapistsRes = await api.get('/therapists');
      console.log('Therapists response:', therapistsRes.data);
      setTherapists(therapistsRes.data.therapists || therapistsRes.data);

      console.log('Fetching assessments...');
      // Fetch onboarding assessments (using the working endpoint)
      const assessmentsRes = await api.get('/assessments/onboarding/list');
      console.log('Assessments response:', assessmentsRes.data);
      const assessmentsData = assessmentsRes.data.assessments || [];
      console.log('Assessments data:', assessmentsData);
      
      // Check completion status for each assessment
      const assessmentsWithStatus = await Promise.all(
        assessmentsData.map(async (assessment) => {
          try {
            console.log(`🔍 Checking completion for therapist: ${assessment.therapistId}`);
            const completionRes = await api.get(`/assessments/check-completion/${assessment.therapistId}`);
            console.log(`✅ Completion data for ${assessment.therapistId}:`, completionRes.data);
            return {
              ...assessment,
              hasCompleted: completionRes.data.hasCompleted,
              responseId: completionRes.data.responseId,
              completedAt: completionRes.data.completedAt
            };
          } catch (error) {
            console.error(`❌ Failed to check completion for ${assessment.therapistId}:`, error);
            console.error(`   Error response:`, error.response?.data);
            return {
              ...assessment,
              hasCompleted: false,
              responseId: null,
              completedAt: null
            };
          }
        })
      );
      
      setAssessments(assessmentsWithStatus);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setAssessments([]);
      setTherapists([]);
    } finally {
      setLoading(false);
    }
  };

  const getTherapistName = (therapistId) => {
    const therapist = therapists.find(t => t.id === therapistId);
    return therapist ? therapist.name : 'Unknown Therapist';
  };

  const getTherapistSpecialty = (therapistId) => {
    const therapist = therapists.find(t => t.id === therapistId);
    return therapist ? therapist.specialty : 'Unknown Specialty';
  };

  const filteredAssessments = selectedTherapist === 'all' 
    ? assessments 
    : assessments.filter(assessment => assessment.therapistId === selectedTherapist);

  const handleStartAssessment = (assessment) => {
    navigate(`/app/assessment/${assessment.id}`, { 
      state: { 
        assessment,
        therapistName: getTherapistName(assessment.therapistId),
        therapistSpecialty: getTherapistSpecialty(assessment.therapistId)
      }
    });
  };

  const handleShowAssessment = (assessment) => {
    console.log('Show assessment clicked:', assessment);
    console.log('ResponseId:', assessment.responseId);
    
    if (!assessment.responseId) {
      console.error('No responseId found for assessment:', assessment);
      alert('Assessment response not found. Please retake the assessment.');
      return;
    }
    
    navigate(`/app/assessment-response/${assessment.responseId}`, {
      state: {
        assessmentName: assessment.name,
        therapistName: getTherapistName(assessment.therapistId),
        therapistSpecialty: getTherapistSpecialty(assessment.therapistId)
      }
    });
  };

  const handleRetakeAssessment = (assessment) => {
    if (window.confirm('This will replace your previous assessment results. Are you sure you want to retake this assessment?')) {
      handleStartAssessment(assessment);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Assessments...</h3>
          <p className="text-gray-600">Preparing your specialized mental health assessments</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto p-6 space-y-8">
        {/* Trial Countdown */}
        <TrialCountdown />
        
        {/* Beautiful Header */}
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-xl border border-blue-100 p-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-500 rounded-3xl mb-6 shadow-lg">
            <Brain className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Specialized Assessments</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-6">
            Take evidence-based assessments tailored to your specific needs. Track your progress and get personalized insights.
          </p>
          <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              <span>Evidence-Based</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4 text-blue-500" />
              <span>Private & Secure</span>
            </div>
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-green-500" />
              <span>Personalized Results</span>
            </div>
          </div>
        </div>

        {/* Therapist Filter */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg">
              <User className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Filter by Therapist</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedTherapist('all')}
              className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 transform hover:scale-105 ${
                selectedTherapist === 'all'
                  ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                  : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              All Therapists
            </button>
            {therapists.map((therapist) => (
              <button
                key={therapist.id}
                onClick={() => setSelectedTherapist(therapist.id)}
                className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 transform hover:scale-105 ${
                  selectedTherapist === therapist.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                {therapist.name}
              </button>
            ))}
          </div>
        </div>

        {/* Assessments Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {filteredAssessments.map((assessment) => (
            <div key={assessment.id} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-100 rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <Brain className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{assessment.name}</h3>
                    <p className="text-sm text-gray-600 flex items-center space-x-2">
                      <Heart className="w-3 h-3 text-red-500" />
                      <span>{getTherapistName(assessment.therapistId)}</span>
                    </p>
                  </div>
                </div>
                {assessment.hasCompleted && (
                  <div className="flex items-center space-x-2 bg-gradient-to-r from-green-50 to-emerald-50 px-3 py-2 rounded-xl border border-green-200">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-semibold text-green-700">Completed</span>
                  </div>
                )}
              </div>
              
              <p className="text-gray-700 leading-relaxed mb-6">{assessment.description}</p>
              
              <div className="flex items-center space-x-6 mb-6">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="p-1 bg-blue-100 rounded-lg">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="font-medium">{assessment.estimatedDuration} minutes</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="p-1 bg-purple-100 rounded-lg">
                    <FileText className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="font-medium">{assessment.questionCount} questions</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
                <span className={`inline-block text-white px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${getSpecialtyColor(getTherapistSpecialty(assessment.therapistId))}`}>
                  {getTherapistSpecialty(assessment.therapistId)}
                </span>
                
                {assessment.hasCompleted ? (
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <button 
                      onClick={() => handleShowAssessment(assessment)}
                      className="flex items-center justify-center space-x-1.5 px-3 py-1.5 border-2 border-blue-200 text-blue-700 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 transform hover:scale-105 text-sm"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span className="font-medium">View Results</span>
                    </button>
                    <button 
                      onClick={() => handleRetakeAssessment(assessment)}
                      className="flex items-center justify-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl text-sm"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span className="font-medium">Retake</span>
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => handleStartAssessment(assessment)}
                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl font-semibold text-sm"
                  >
                    <span>Start Assessment</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredAssessments.length === 0 && (
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl border border-gray-100 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl mb-4">
              <FileText className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Assessments Available</h3>
            <p className="text-gray-600 mb-6">Try selecting a different therapist or check back later for new assessments.</p>
            <button
              onClick={() => setSelectedTherapist('all')}
              className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              View All Assessments
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Assessments; 